"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { useListingFacetCounts } from "@/app/(saas)/(app)/businesses/_components/filters/use-listing-facet-counts";
import {
  type FilterValue,
  RangeFilter,
} from "@/components/common/range-filter";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterLabel } from "@/app/(saas)/(app)/businesses/_components/filters/filter-label";
import { useAtom } from "jotai";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

const ITEMS: FilterValue[] = [
  { label: "$25,000", value: BigInt(25_000), stringValue: "25000" },
  { label: "$50,000", value: BigInt(50_000), stringValue: "50000" },
  { label: "$100,000", value: BigInt(100_000), stringValue: "100000" },
  { label: "$200,000", value: BigInt(200_000), stringValue: "200000" },
  { label: "$250,000", value: BigInt(250_000), stringValue: "250000" },
  { label: "$500,000", value: BigInt(500_000), stringValue: "500000" },
  { label: "$1,000,000", value: BigInt(1_000_000), stringValue: "1000000" },
  { label: "$2,000,000", value: BigInt(2_000_000), stringValue: "2000000" },
  { label: "$5,000,000", value: BigInt(5_000_000), stringValue: "5000000" },
  { label: "$10,000,000", value: BigInt(10_000_000), stringValue: "10000000" },
  { label: "$20,000,000", value: BigInt(20_000_000), stringValue: "20000000" },
  { label: "$50,000,000", value: BigInt(50_000_000), stringValue: "50000000" },
  {
    label: "$100,000,000",
    value: BigInt(100_000_000),
    stringValue: "100000000",
  },
];

const SDE_MULTIPLE_ITEMS: FilterValue[] = [
  { label: "1x", value: 1, stringValue: "1" },
  { label: "1.5x", value: 1.5, stringValue: "1.5" },
  { label: "2x", value: 2, stringValue: "2" },
  { label: "2.5x", value: 2.5, stringValue: "2.5" },
  { label: "3x", value: 3, stringValue: "3" },
  { label: "3.5x", value: 3.5, stringValue: "3.5" },
  { label: "4x", value: 4, stringValue: "4" },
  { label: "5x", value: 5, stringValue: "5" },
  { label: "6x", value: 6, stringValue: "6" },
];

const REVENUE_MULTIPLE_ITEMS: FilterValue[] = [
  { label: "0.25x", value: 0.25, stringValue: "0.25" },
  { label: "0.5x", value: 0.5, stringValue: "0.5" },
  { label: "0.75x", value: 0.75, stringValue: "0.75" },
  { label: "1x", value: 1, stringValue: "1" },
  { label: "1.5x", value: 1.5, stringValue: "1.5" },
  { label: "2x", value: 2, stringValue: "2" },
  { label: "2.5x", value: 2.5, stringValue: "2.5" },
  { label: "3x", value: 3, stringValue: "3" },
];

function buildCountMaps(
  rangeCounts: { gte: Record<string, number>; lte: Record<string, number> } | undefined,
  items: typeof ITEMS,
) {
  if (!rangeCounts) return { min: undefined, max: undefined };

  const minCounts: Record<string, number> = {};
  const maxCounts: Record<string, number> = {};

  for (const item of items) {
    minCounts[item.stringValue] = rangeCounts.gte[item.stringValue] ?? 0;
    maxCounts[item.stringValue] = rangeCounts.lte[item.stringValue] ?? 0;
  }

  return { min: minCounts, max: maxCounts };
}

export function PriceAndFinancialsFilter({
  mode = "all",
}: {
  mode?: "all" | "financials" | "multiples" | "dealTerms";
} = {}) {
  const t = useTranslations();
  const showPrice = mode === "all";
  const showFinancialRanges = mode === "all" || mode === "financials";
  const showMultiples = mode === "all" || mode === "multiples";
  const showDealTerms = mode === "all" || mode === "dealTerms";

  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const facets = useListingFacetCounts();

  const MIN_ITEMS: FilterValue[] = [
    {
      label: t("saas.search.filters.common.noMinimum"),
      value: null,
      stringValue: "_null",
    },
    ...ITEMS,
  ];

  const MAX_ITEMS: FilterValue[] = [
    {
      label: t("saas.search.filters.common.noMaximum"),
      value: null,
      stringValue: "_null",
    },
    ...ITEMS,
  ];

  const rc = facets.data?.rangeCounts;
  const MIN_SDE_MULTIPLE_ITEMS: FilterValue[] = [
    {
      label: t("saas.search.filters.common.noMinimum"),
      value: null,
      stringValue: "_null",
    },
    ...SDE_MULTIPLE_ITEMS,
  ];
  const MAX_SDE_MULTIPLE_ITEMS: FilterValue[] = [
    {
      label: t("saas.search.filters.common.noMaximum"),
      value: null,
      stringValue: "_null",
    },
    ...SDE_MULTIPLE_ITEMS,
  ];
  const MIN_REVENUE_MULTIPLE_ITEMS: FilterValue[] = [
    {
      label: t("saas.search.filters.common.noMinimum"),
      value: null,
      stringValue: "_null",
    },
    ...REVENUE_MULTIPLE_ITEMS,
  ];
  const MAX_REVENUE_MULTIPLE_ITEMS: FilterValue[] = [
    {
      label: t("saas.search.filters.common.noMaximum"),
      value: null,
      stringValue: "_null",
    },
    ...REVENUE_MULTIPLE_ITEMS,
  ];

  const priceCounts = useMemo(
    () => buildCountMaps(rc?.price, ITEMS),
    [rc?.price],
  );
  const revenueCounts = useMemo(
    () => buildCountMaps(rc?.revenue, ITEMS),
    [rc?.revenue],
  );
  const profitCounts = useMemo(
    () => buildCountMaps(rc?.profit, ITEMS),
    [rc?.profit],
  );
  const sdeCounts = useMemo(
    () => buildCountMaps(rc?.sde, ITEMS),
    [rc?.sde],
  );
  const downPaymentCounts = useMemo(
    () => buildCountMaps(rc?.downPayment, ITEMS),
    [rc?.downPayment],
  );
  const sdeMultipleCounts = useMemo(
    () => buildCountMaps(rc?.sdeMultiple, SDE_MULTIPLE_ITEMS),
    [rc?.sdeMultiple],
  );
  const revenueMultipleCounts = useMemo(
    () => buildCountMaps(rc?.revenueMultiple, REVENUE_MULTIPLE_ITEMS),
    [rc?.revenueMultiple],
  );

  function getValue(filterValue: FilterValue | null) {
    if (!filterValue) {
      return null;
    }

    if (!filterValue.value) {
      return null;
    }

    return filterValue.value as bigint;
  }

  function getNumberValue(filterValue: FilterValue | null) {
    if (!filterValue || filterValue.value === null) {
      return null;
    }

    return Number(filterValue.value);
  }

  function handlePriceChange(min: FilterValue | null, max: FilterValue | null) {
    setFilters((prev) => ({
      ...prev,
      priceRange: {
        min: getValue(min),
        max: getValue(max),
      },
    }));
  }

  function handleRevenueChange(
    min: FilterValue | null,
    max: FilterValue | null,
  ) {
    setFilters((prev) => ({
      ...prev,
      revenueRange: {
        min: getValue(min),
        max: getValue(max),
      },
    }));
  }

  function handleProfitChange(
    min: FilterValue | null,
    max: FilterValue | null,
  ) {
    setFilters((prev) => ({
      ...prev,
      profitRange: {
        min: getValue(min),
        max: getValue(max),
      },
    }));
  }

  function handleSdeChange(min: FilterValue | null, max: FilterValue | null) {
    setFilters((prev) => ({
      ...prev,
      sdeRange: {
        min: getValue(min),
        max: getValue(max),
      },
    }));
  }

  function handleSdeMultipleChange(
    min: FilterValue | null,
    max: FilterValue | null,
  ) {
    setFilters((prev) => ({
      ...prev,
      sdeMultipleRange: {
        min: getNumberValue(min),
        max: getNumberValue(max),
      },
    }));
  }

  function handleRevenueMultipleChange(
    min: FilterValue | null,
    max: FilterValue | null,
  ) {
    setFilters((prev) => ({
      ...prev,
      revenueMultipleRange: {
        min: getNumberValue(min),
        max: getNumberValue(max),
      },
    }));
  }

  function handleDownPaymentChange(
    min: FilterValue | null,
    max: FilterValue | null,
  ) {
    setFilters((prev) => ({
      ...prev,
      downPaymentRange: {
        min: getValue(min),
        max: getValue(max),
      },
    }));
  }

  function resetPriceAndFinancials() {
    setFilters((prev) => ({
      ...prev,
      ...(showPrice ? { priceRange: { min: null, max: null } } : {}),
      ...(showFinancialRanges
        ? {
            revenueRange: { min: null, max: null },
            profitRange: { min: null, max: null },
            sdeRange: { min: null, max: null },
          }
        : {}),
      ...(showMultiples
        ? {
            sdeMultipleRange: { min: null, max: null },
            revenueMultipleRange: { min: null, max: null },
            includeMissingMultiples: true,
          }
        : {}),
      ...(showDealTerms
        ? {
            downPaymentRange: { min: null, max: null },
          }
        : {}),
    }));
  }

  return (
    <div className="flex flex-col gap-4">
      {showPrice && (
        <RangeFilter
          type="multi"
          minItems={MIN_ITEMS}
          maxItems={MAX_ITEMS}
          defaultMinValue={MIN_ITEMS.find(
            (item) => item.value === filters.priceRange?.min,
          )}
          defaultMaxValue={MAX_ITEMS.find(
            (item) => item.value === filters.priceRange?.max,
          )}
          onChange={handlePriceChange}
          minCounts={priceCounts.min}
          maxCounts={priceCounts.max}
          countsLoading={facets.isLoading}
          leading={
            <FilterLabel hint={t("saas.search.filters.fields.pricesAndFinancials.price.hint")}>
              {t("saas.search.filters.fields.pricesAndFinancials.price.label")}
            </FilterLabel>
          }
        />
      )}

      {showFinancialRanges && (
        <RangeFilter
          type="multi"
          minItems={MIN_ITEMS}
          maxItems={MAX_ITEMS}
          defaultMinValue={MIN_ITEMS.find(
            (item) => item.value === filters.revenueRange?.min,
          )}
          defaultMaxValue={MAX_ITEMS.find(
            (item) => item.value === filters.revenueRange?.max,
          )}
          onChange={handleRevenueChange}
          minCounts={revenueCounts.min}
          maxCounts={revenueCounts.max}
          countsLoading={facets.isLoading}
          leading={
            <FilterLabel hint={t("saas.search.filters.fields.pricesAndFinancials.revenue.hint")}>
              {t("saas.search.filters.fields.pricesAndFinancials.revenue.label")}
            </FilterLabel>
          }
        />
      )}

      {showFinancialRanges && (
        <RangeFilter
          type="multi"
          minItems={MIN_ITEMS}
          maxItems={MAX_ITEMS}
          defaultMinValue={MIN_ITEMS.find(
            (item) => item.value === filters.profitRange?.min,
          )}
          defaultMaxValue={MAX_ITEMS.find(
            (item) => item.value === filters.profitRange?.max,
          )}
          onChange={handleProfitChange}
          minCounts={profitCounts.min}
          maxCounts={profitCounts.max}
          countsLoading={facets.isLoading}
          leading={
            <FilterLabel hint={t("saas.search.filters.fields.pricesAndFinancials.profit.hint")}>
              {t("saas.search.filters.fields.pricesAndFinancials.profit.label")}
            </FilterLabel>
          }
        />
      )}

      {showFinancialRanges && (
        <RangeFilter
          type="multi"
          minItems={MIN_ITEMS}
          maxItems={MAX_ITEMS}
          defaultMinValue={MIN_ITEMS.find(
            (item) => item.value === filters.sdeRange?.min,
          )}
          defaultMaxValue={MAX_ITEMS.find(
            (item) => item.value === filters.sdeRange?.max,
          )}
          onChange={handleSdeChange}
          minCounts={sdeCounts.min}
          maxCounts={sdeCounts.max}
          countsLoading={facets.isLoading}
          leading={
            <FilterLabel hint={t("saas.search.filters.fields.pricesAndFinancials.sde.hint")}>
              {t("saas.search.filters.fields.pricesAndFinancials.sde.label")}
            </FilterLabel>
          }
        />
      )}

      {showMultiples && (
        <RangeFilter
          type="multi"
          minItems={MIN_SDE_MULTIPLE_ITEMS}
          maxItems={MAX_SDE_MULTIPLE_ITEMS}
          defaultMinValue={MIN_SDE_MULTIPLE_ITEMS.find(
            (item) => item.value === filters.sdeMultipleRange?.min,
          )}
          defaultMaxValue={MAX_SDE_MULTIPLE_ITEMS.find(
            (item) => item.value === filters.sdeMultipleRange?.max,
          )}
          onChange={handleSdeMultipleChange}
          minCounts={sdeMultipleCounts.min}
          maxCounts={sdeMultipleCounts.max}
          countsLoading={facets.isLoading}
          leading={
            <FilterLabel hint={t("saas.search.filters.fields.pricesAndFinancials.sdeMultiple.hint")}>
              {t("saas.search.filters.fields.pricesAndFinancials.sdeMultiple.label")}
            </FilterLabel>
          }
        />
      )}

      {showMultiples && (
        <RangeFilter
          type="multi"
          minItems={MIN_REVENUE_MULTIPLE_ITEMS}
          maxItems={MAX_REVENUE_MULTIPLE_ITEMS}
          defaultMinValue={MIN_REVENUE_MULTIPLE_ITEMS.find(
            (item) => item.value === filters.revenueMultipleRange?.min,
          )}
          defaultMaxValue={MAX_REVENUE_MULTIPLE_ITEMS.find(
            (item) => item.value === filters.revenueMultipleRange?.max,
          )}
          onChange={handleRevenueMultipleChange}
          minCounts={revenueMultipleCounts.min}
          maxCounts={revenueMultipleCounts.max}
          countsLoading={facets.isLoading}
          leading={
            <FilterLabel
              hint={t("saas.search.filters.fields.pricesAndFinancials.revenueMultiple.hint")}
            >
              {t(
                "saas.search.filters.fields.pricesAndFinancials.revenueMultiple.label",
              )}
            </FilterLabel>
          }
        />
      )}

      {showMultiples && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeMissingMultiples"
              checked={filters.includeMissingMultiples}
              onCheckedChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  includeMissingMultiples: Boolean(value),
                }))}
              className="w-5 h-5 border-2 rounded-[6px]"
            />
            <FilterLabel
              htmlFor="includeMissingMultiples"
              hint={t(
                "saas.search.filters.fields.pricesAndFinancials.includeMissingMultiples.hint",
              )}
            >
              {t(
                "saas.search.filters.fields.pricesAndFinancials.includeMissingMultiples.label",
              )}
            </FilterLabel>
          </div>
        </div>
      )}

      {showDealTerms && (
        <RangeFilter
          type="multi"
          minItems={MIN_ITEMS}
          maxItems={MAX_ITEMS}
          defaultMinValue={MIN_ITEMS.find(
            (item) => item.value === filters.downPaymentRange?.min,
          )}
          defaultMaxValue={MAX_ITEMS.find(
            (item) => item.value === filters.downPaymentRange?.max,
          )}
          onChange={handleDownPaymentChange}
          minCounts={downPaymentCounts.min}
          maxCounts={downPaymentCounts.max}
          countsLoading={facets.isLoading}
          leading={
            <FilterLabel hint={t("saas.search.filters.fields.pricesAndFinancials.downPayment.hint")}>
              {t("saas.search.filters.fields.pricesAndFinancials.downPayment.label")}
            </FilterLabel>
          }
        />
      )}

      <div className="flex items-center justify-between border-t pt-3">
        <button
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
          onClick={resetPriceAndFinancials}
        >
          Reset
        </button>

        <span className="text-xs text-muted-foreground">
          {facets.isLoading ? (
            <span className="inline-block w-8 h-3 bg-muted animate-pulse rounded" />
          ) : (
            <span className="font-data tabular-nums text-foreground">
              {facets.data?.total ?? 0}
            </span>
          )}{" "}
          businesses match
        </span>
      </div>
    </div>
  );
}
